import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete } from "antd";
import { createPlaceMarkerStyle } from "../utils/placeMarkerStyle";
import { useTranslation } from "react-i18next";
import ClickOnMap from "./clickOnMap/ClickOnMap";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { autosuggestAddress, geocodeAddress } from "../utils/hereGeocoding";
import { normalizeHereLanguage } from "../utils/hereMaps";
import { validCoordinates, parseCoordinatePair } from "../utils/locationValidation";

// Flat directory-form value; coordinates are never user-facing fields.
export default function LocationMapInput({ value = {}, onChange, suggestInitialAddress = false, showErrors = false }) {
  const { t, i18n } = useTranslation();
  const { hereApiKey } = useGlobalSettings();
  const [options, setOptions] = useState([]);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const sequence = useRef(0);
  const timer = useRef(null);
  const attemptedInitial = useRef(false);
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const placeType = value.category || value.placeTypes?.find(type => !["DEPOT", "TERMINAL"].includes(type)) || "DELIVERY_POINT";
  const markerStyle = useMemo(() => createPlaceMarkerStyle(placeType), [placeType]);
  useEffect(() => () => { sequence.current++; clearTimeout(timer.current); }, []);
  const language = normalizeHereLanguage(i18n.resolvedLanguage || i18n.language);
  const located = validCoordinates(value.latitude, value.longitude);
  const visibleError = (showErrors || touched) && (error || (!located ? "placeForm.coordinates" : ""));
  useEffect(() => {
    if (!suggestInitialAddress || attemptedInitial.current || located || !hereApiKey) return;
    const address = String(value.streetName || "").trim();
    if (!address) return;
    attemptedInitial.current = true;
    const request = ++sequence.current;
    const query = [address, value.postalCode, value.cityName, value.countryCode].filter(Boolean).join(", ");
    void geocodeAddress(query, hereApiKey, { language }).then(point => {
      if (request !== sequence.current) return;
      if (!point || point.lookupFailed) { setError("placeForm.searchError"); return; }
      const current = latest.current;
      if (current.value.streetName !== value.streetName || validCoordinates(current.value.latitude, current.value.longitude)) return;
      current.onChange({ ...current.value, latitude: point.lat, longitude: point.lng,
        streetName: point.address || address, cityName: point.cityName || "",
        postalCode: point.postalCode || "", countryCode: point.countryCode || "" });
    });
  }, [suggestInitialAddress, located, hereApiKey, language, value.streetName, value.postalCode, value.cityName, value.countryCode]);
  const select = point => {
    setSelectionVersion(version => version + 1);
    sequence.current++;
    clearTimeout(timer.current);
    setOptions([]);
    setError(point.lookupFailed ? "placeForm.searchError" : "");
    onChange({ ...latest.current.value, streetName: point.address || "", latitude: point.lookupFailed ? null : point.lat, longitude: point.lookupFailed ? null : point.lng,
      cityName: point.cityName || "", postalCode: point.postalCode || "", countryCode: point.countryCode || "" });
  };
  const search = query => {
    setSelectionVersion(version => version + 1);
    const request = ++sequence.current;
    clearTimeout(timer.current);
    setOptions([]);
    setError("");
    // Editing the address invalidates the previous pin until another location is selected.
    onChange({ ...value, streetName: query, latitude: null, longitude: null, cityName: "", postalCode: "", countryCode: "" });
    if (parseCoordinatePair(query)?.invalid) { setError("placeForm.coordinates"); return; }
    if (query.trim().length < 3) return;
    timer.current = setTimeout(async () => {
      try {
        const results = await autosuggestAddress(query, hereApiKey, { language, throwOnError: true,
          at: located ? { lat: value.latitude, lng: value.longitude } : null });
        if (request === sequence.current) {
          setOptions(results.map((point, index) => ({ value: `${index}:${point.address}`, label: point.address, point })));
          if (!results.length) setError("placeForm.searchError");
        }
      } catch { if (request === sequence.current) setError("placeForm.searchError"); }
    }, 300);
  };
  return <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
    <AutoComplete value={value.streetName || ""} options={options} onSearch={search}
      onBlur={() => setTouched(true)} status={visibleError ? "error" : undefined} title={visibleError ? t(visibleError) : t("directory.address")}
      onSelect={(_, option) => select(option.point)} allowClear onClear={() => search("")}
      placeholder={t("directory.address")} style={{ width: "100%" }} />
    <ClickOnMap selectionVersion={selectionVersion} activeIndex={0} basePoints={located ? [{ lat: Number(value.latitude), lng: Number(value.longitude), address: value.streetName }] : []}
      markerStyle={markerStyle} onSelectionStart={() => {
        sequence.current++; clearTimeout(timer.current); setOptions([]); attemptedInitial.current = true;
        onChange({ ...latest.current.value, latitude: null, longitude: null });
      }} onBasePointUpdate={select} onClearPoints={() => { sequence.current++; attemptedInitial.current = true; onChange({ ...value, latitude: null, longitude: null }); }} />
  </div>;
}
